import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Buttons in the console are rectangles with hairline borders, not pills.
 * Variants map to *intent*, and intent is load-bearing here: `danger` is
 * reserved for operations that move money or destroy records, so an operator
 * can trust the colour.
 */
export type ButtonVariant =
  | 'primary' // the one affirmative action on a screen
  | 'default' // ordinary action
  | 'ghost' // toolbar / icon actions, no chrome until hover
  | 'danger' // destructive or money-moving
  | 'link'; // inline, text-only

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-signal text-ink-inverse border border-signal font-medium hover:bg-signal-hover active:brightness-95',
  default:
    'bg-surface text-ink border border-line hover:bg-surface-hover hover:border-line-strong active:bg-surface-active',
  ghost:
    'bg-transparent text-ink-muted border border-transparent hover:bg-surface-hover hover:text-ink active:bg-surface-active',
  danger:
    'bg-transparent text-negative border border-negative/45 hover:bg-negative-muted hover:border-negative active:brightness-95',
  link: 'bg-transparent text-signal-ink border-0 underline-offset-2 hover:underline p-0 h-auto',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-6 px-1.5 text-2xs gap-1 rounded-xs',
  sm: 'h-7 px-2 text-xs gap-1.5 rounded-sm',
  md: 'h-8 px-3 text-sm gap-1.5 rounded',
  lg: 'h-10 px-4 text-base gap-2 rounded',
  icon: 'h-8 w-8 rounded justify-center',
  'icon-sm': 'h-7 w-7 rounded-sm justify-center',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders a spinner and blocks interaction. */
  loading?: boolean;
  /** Render as the single child element instead of a <button> (e.g. a Link). */
  asChild?: boolean;
  icon?: ReactNode;
  /** Keyboard hint rendered on the trailing edge, e.g. `⌘S`. */
  kbd?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'default',
    size = 'md',
    loading = false,
    asChild = false,
    icon,
    kbd,
    className,
    children,
    disabled,
    ...props
  },
  ref
) {
  const classes = cn(
    'inline-flex shrink-0 select-none items-center whitespace-nowrap',
    'transition-[background-color,border-color,color,opacity] duration-100',
    'disabled:pointer-events-none disabled:opacity-40',
    VARIANTS[variant],
    SIZES[size],
    className
  );

  const leading = loading ? (
    <Loader2 className="size-3.5 animate-spin" aria-hidden />
  ) : (
    icon
  );
  const trailing = kbd ? (
    <kbd className="text-2xs ml-auto pl-1.5 font-mono tracking-tight opacity-55">
      {kbd}
    </kbd>
  ) : null;

  // `asChild` renders the caller's element (usually a <Link>) with our styling.
  // Radix's Slot accepts exactly one child, so the icon and kbd decorations have
  // to move *inside* that child rather than sit beside it — passing three
  // children to Slot throws at runtime.
  if (asChild) {
    const child = Children.only(children);
    if (!isValidElement(child)) {
      throw new Error('<Button asChild> expects a single element child.');
    }
    const typed = child as ReactElement<{ children?: ReactNode }>;
    return (
      <Slot ref={ref} className={classes} data-loading={loading || undefined} {...props}>
        {cloneElement(
          typed,
          undefined,
          <>
            {leading}
            {typed.props.children}
            {trailing}
          </>
        )}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      data-loading={loading || undefined}
      className={classes}
      {...props}
    >
      {leading}
      {children}
      {trailing}
    </button>
  );
});

/** A run of buttons welded into one control (segmented / split actions). */
export function ButtonGroup({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'inline-flex items-center',
        '[&>*]:rounded-none [&>*:first-child]:rounded-l [&>*:last-child]:rounded-r',
        '[&>*:focus-visible]:relative [&>*:hover]:relative [&>*:not(:first-child)]:-ml-px',
        className
      )}
    >
      {children}
    </div>
  );
}
